
import { useState, useEffect } from 'react';
import Button from '../base/Button';

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [showExpertModal, setShowExpertModal] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setScrolled(scrollPosition > 50);
      
      // Enhanced section detection with improved offset calculations
      const sections = ['home', 'services', 'about', 'products', 'testimonials', 'contact'];
      const headerHeight = 80;
      
      for (let i = sections.length - 1; i >= 0; i--) {
        const section = document.getElementById(sections[i]);
        if (section) {
          const sectionTop = section.offsetTop - headerHeight;
          const sectionBottom = sectionTop + section.offsetHeight;
          
          if (scrollPosition >= sectionTop && scrollPosition < sectionBottom) {
            if (activeSection !== sections[i]) {
              setActiveSection(sections[i]);
            }
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial check
    return () => window.removeEventListener('scroll', handleScroll);
  }, [activeSection]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const scrollToSection = (sectionId: string) => {
    const section = document.getElementById(sectionId);
    if (section) {
      const headerHeight = 80;
      const elementPosition = section.offsetTop - headerHeight;
      
      window.scrollTo({
        top: elementPosition,
        behavior: 'smooth'
      });
      
      setActiveSection(sectionId);
    }
    setIsMenuOpen(false);
  };

  const handleEmailClick = () => {
    window.location.href =
      'mailto:info@djthaika.in?subject=Inquiry about EV Charging Solutions&body=Hello DJT HAIKA team,%0D%0A%0D%0AI am interested in learning more about your EV charging solutions.%0D%0A%0D%0APlease contact me with more information.%0D%0A%0D%0AThank you!';
  };

  const handleGetQuote = () => {
    window.location.href = 'tel:18005700229';
  };

  const handlePhoneClick = () => {
    window.location.href = 'tel:18005700229';
  };

  const handleContactExpert = () => {
    setShowExpertModal(true);
  };

  const handlePhoneCall = () => {
    window.location.href = 'tel:18005700229';
    setShowExpertModal(false);
  };

  const handleEmailExpert = () => {
    window.location.href = 'mailto:info@djthaika.in?subject=Expert Consultation Request&body=Hello DJT HAIKA team,%0D%0A%0D%0AI need expert consultation for EV charging solutions.%0D%0A%0D%0APlease contact me at your earliest convenience.%0D%0A%0D%0AThank you!';
    setShowExpertModal(false);
  };

  const handleScheduleCall = () => {
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
    setShowExpertModal(false);
  };

  const handleWhatsAppChat = () => {
    window.open('https://wa.me/911234567890?text=Hi, I need expert consultation for EV charging solutions. Please help me choose the right charging infrastructure for my needs.', '_blank');
    setShowExpertModal(false);
  };

  const handleLiveChat = () => {
    // Simulate opening live chat - this would integrate with actual chat system
    const chatBot = document.querySelector('[class*="chat"]') || document.querySelector('button[class*="chat"]');
    if (chatBot) {
      (chatBot as HTMLElement).click();
    }
    setShowExpertModal(false);
  };

  return (
    <>
      <header className={`fixed top-0 left-0 right-0 w-full backdrop-blur-md shadow-lg border-b z-[9999] transition-all duration-300 relative overflow-hidden ${
        scrolled 
          ? 'bg-slate-900/95 border-cyan-500/30' 
          : 'bg-slate-900/85 border-cyan-500/20'
      }`} style={{ position: 'fixed' }}>
        
        {/* Enhanced Animated Background for Header */}
        <div className="absolute inset-0 overflow-hidden">
          {/* Enhanced Floating Tech Particles with More Animation */}
          <div className="absolute top-2 left-10 w-1.5 h-1.5 bg-cyan-400/40 rounded-full animate-bounce opacity-60 particle-glow" style={{animationDelay: '0s', animationDuration: '3s'}}></div>
          <div className="absolute top-4 right-20 w-1 h-1 bg-green-400/30 rounded-full animate-ping opacity-50 particle-glow" style={{animationDelay: '1s'}}></div>
          <div className="absolute bottom-2 left-1/4 w-1.5 h-1.5 bg-blue-500/40 rounded-full animate-pulse opacity-70 particle-glow" style={{animationDelay: '2s'}}></div>
          <div className="absolute top-1/2 right-1/3 w-1 h-1 bg-purple-400/30 rounded-full animate-bounce opacity-40 particle-glow" style={{animationDelay: '0.5s', animationDuration: '2.5s'}}></div>
          <div className="absolute bottom-1 right-10 w-1 h-1 bg-yellow-400/35 rounded-full animate-ping opacity-45 particle-glow" style={{animationDelay: '1.5s'}}></div>
          <div className="absolute top-3 left-1/3 w-1.5 h-1.5 bg-indigo-400/45 rounded-full animate-pulse opacity-55 particle-glow" style={{animationDelay: '3s'}}></div>
          <div className="absolute bottom-3 right-1/4 w-1 h-1 bg-pink-400/40 rounded-full animate-bounce opacity-50 particle-glow" style={{animationDelay: '2.5s', animationDuration: '2.8s'}}></div>
          <div className="absolute top-1/4 left-16 w-1.5 h-1.5 bg-teal-400/35 rounded-full animate-ping opacity-60 particle-glow" style={{animationDelay: '4s'}}></div>
          
          {/* Enhanced Moving Energy Lines with Wave Effects */}
          <div className="absolute top-0 left-0 w-20 h-px bg-gradient-to-r from-transparent via-cyan-400/20 to-transparent opacity-40 header-energy-line-right energy-pulse"></div>
          <div className="absolute bottom-0 right-0 w-24 h-px bg-gradient-to-l from-transparent via-green-400/15 to-transparent opacity-30 header-energy-line-left energy-pulse" style={{animationDelay: '1s'}}></div>
          <div className="absolute top-1/2 left-0 w-16 h-px bg-gradient-to-r from-transparent via-blue-400/25 to-transparent opacity-35 header-energy-line-right energy-pulse" style={{animationDelay: '2s'}}></div>
          <div className="absolute bottom-1/2 right-0 w-18 h-px bg-gradient-to-l from-transparent via-purple-400/20 to-transparent opacity-40 header-energy-line-left energy-pulse" style={{animationDelay: '3s'}}></div>
          
          {/* Enhanced Rotating Tech Elements with Multiple Layers */}
          <div className="absolute top-2 right-8 w-6 h-6 border border-cyan-400/15 rounded-full animate-spin opacity-20 tech-ring-glow" style={{animationDuration: '8s'}}></div>
          <div className="absolute bottom-2 left-8 w-8 h-8 border border-green-400/10 rounded-full animate-spin opacity-15 tech-ring-glow" style={{animationDuration: '12s', animationDirection: 'reverse'}}></div>
          <div className="absolute top-1/2 right-12 w-5 h-5 border border-blue-400/20 rounded-full animate-spin opacity-25 tech-ring-glow" style={{animationDuration: '6s'}}></div>
          <div className="absolute bottom-1/3 left-12 w-7 h-7 border border-purple-400/12 rounded-full animate-spin opacity-18 tech-ring-glow" style={{animationDuration: '10s', animationDirection: 'reverse'}}></div>
          
          {/* Floating Data Streams */}
          <div className="absolute top-6 left-20 w-12 h-0.5 bg-gradient-to-r from-cyan-400/30 to-transparent data-stream opacity-50" style={{animationDelay: '0s'}}></div>
          <div className="absolute bottom-8 right-24 w-14 h-0.5 bg-gradient-to-l from-green-400/25 to-transparent data-stream-reverse opacity-45" style={{animationDelay: '1.5s'}}></div>
          <div className="absolute top-1/3 left-32 w-10 h-0.5 bg-gradient-to-r from-blue-400/35 to-transparent data-stream opacity-40" style={{animationDelay: '3s'}}></div>
          
          {/* Enhanced Header Circuit Pattern with Animation */}
          <div className="absolute inset-0 opacity-5">
            <div className="header-circuit-pattern-enhanced"></div>
          </div>
          
          {/* Pulsing Network Nodes */}
          <div className="absolute top-4 left-1/4 w-2 h-2 bg-cyan-400/30 rounded-full network-node opacity-50" style={{animationDelay: '0s'}}></div>
          <div className="absolute bottom-4 right-1/4 w-1.5 h-1.5 bg-green-400/25 rounded-full network-node opacity-45" style={{animationDelay: '2s'}}></div>
          <div className="absolute top-1/2 left-1/2 w-2 h-2 bg-blue-400/35 rounded-full network-node opacity-55" style={{animationDelay: '4s'}}></div>
          
          {/* Energy Connecting Lines */}
          <div className="absolute top-4 left-1/4 connecting-line-1"></div>
          <div className="absolute bottom-4 right-1/4 connecting-line-2"></div>
          <div className="absolute top-1/2 left-1/2 connecting-line-3"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Streamlined Top Contact Bar */}
          <div className={`hidden md:flex items-center justify-between text-sm border-b py-1.5 transition-all duration-300 ${
            scrolled ? 'border-cyan-500/20' : 'border-cyan-500/15'
          }`}>
            <div className="flex items-center space-x-6 text-gray-300">
              <div
                className="flex items-center hover:text-cyan-400 transition-colors duration-300 cursor-pointer group"
                onClick={handleEmailClick}
              >
                <i className="ri-mail-line text-cyan-400 mr-2 text-sm"></i>
                <span className="text-xs">info@djthaika.in</span>
              </div>

              <div
                className="flex items-center hover:text-green-400 transition-colors duration-300 cursor-pointer group"
                onClick={handlePhoneClick}
              >
                <i className="ri-phone-line text-green-400 mr-2 text-sm"></i>
                <span className="text-xs">1-800-570-0229</span>
              </div>

              <div className="flex items-center text-blue-400">
                <i className="ri-map-pin-line mr-2 text-sm"></i>
                <span className="text-xs">Pune • Chennai • Delhi • Haryana</span>
              </div>

              <div className="flex items-center text-blue-400">
                <i className="ri-customer-service-2-line mr-2 text-sm"></i>
                <span className="text-xs">24/7 Support</span>
              </div>
            </div>

            <div className="flex items-center space-x-1">
              {[
                { icon: 'ri-facebook-fill', url: 'https://facebook.com/djthaika', color: 'text-blue-500 hover:text-blue-400' },
                { icon: 'ri-twitter-fill', url: 'https://twitter.com/djthaika', color: 'text-sky-500 hover:text-sky-400' },
                { icon: 'ri-linkedin-fill', url: 'https://linkedin.com/company/djthaika', color: 'text-blue-600 hover:text-blue-500' },
                { icon: 'ri-instagram-fill', url: 'https://instagram.com/djthaika', color: 'text-pink-500 hover:text-pink-400' }
              ].map((social, idx) => (
                <div
                  key={idx}
                  className={`w-5 h-5 flex items-center justify-center rounded-full bg-white/10 ${social.color} transition-all duration-300 cursor-pointer hover:scale-110 group relative`}
                  onClick={() => window.open(social.url, '_blank')}
                >
                  <i className={`${social.icon} text-xs`}></i>
                </div>
              ))}
            </div>
          </div>

          {/* Streamlined Main Header */}
          <div className="flex items-center justify-between h-16">
            {/* Cleaner Logo Section */}
            <div
              className="flex items-center cursor-pointer group"
              onClick={() => scrollToSection('home')}
            >
              <div className="relative">
                <div className="relative transform transition-all duration-500 group-hover:scale-105">
                  <div className="relative bg-slate-800/80 backdrop-blur-sm rounded-lg p-2 border border-cyan-400/20 group-hover:border-cyan-400/40 transition-all duration-300">
                    <img 
                      src="https://static.readdy.ai/image/b8a021112837a070edfa44e380da93b7/83669deabdd1c2a98c79f1755e4f7c6c.png" 
                      alt="DJT HAIKA Logo" 
                      className="h-12 w-auto filter brightness-110 group-hover:brightness-125 transition-all duration-300"
                    />
                  </div>
                  
                  {/* Simplified tech indicator */}
                  <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-gradient-to-r from-green-400 to-cyan-400 rounded-full animate-pulse"></div>
                </div>
              </div>
            </div>

            {/* Streamlined Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-1">
              {[
                { id: 'home', label: 'Home', icon: 'ri-home-4-line' },
                { id: 'services', label: 'Services', icon: 'ri-flashlight-line' },
                { id: 'about', label: 'About', icon: 'ri-information-line' },
                { id: 'products', label: 'Products', icon: 'ri-car-line' },
                { id: 'contact', label: 'Contact', icon: 'ri-customer-service-line' }
              ].map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`font-medium transition-all duration-300 rounded-lg px-4 py-2 text-sm whitespace-nowrap hover:scale-105 group ${
                    activeSection === item.id
                      ? 'text-white bg-gradient-to-r from-cyan-600/40 to-blue-600/40'
                      : 'text-gray-300 hover:text-white hover:bg-white/10'
                  }`}
                >
                  <span className="flex items-center">
                    <i className={`${item.icon} mr-2 text-sm ${
                      activeSection === item.id ? 'text-cyan-300' : ''
                    }`}></i>
                    {item.label}
                  </span>
                </button>
              ))}
            </nav>

            {/* Compact CTA Section */}
            <div className="hidden lg:flex items-center space-x-3">
              {/* Compact Expert Section */}
              <div className="flex items-center space-x-2 px-3 py-2 bg-gradient-to-r from-green-600/20 to-emerald-600/20 rounded-lg border border-green-500/30">
                <div className="w-6 h-6 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center relative">
                  <i className="ri-user-star-line text-white text-xs"></i>
                  <div className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                </div>
                <div className="text-left">
                  <p className="text-white text-xs font-medium">Expert Online</p>
                </div>
                <button
                  onClick={handleContactExpert}
                  className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-medium transition-all duration-300 whitespace-nowrap hover:scale-105 group text-xs px-3 py-1.5 rounded-md cursor-pointer"
                >
                  <i className="ri-customer-service-2-line mr-1"></i>
                  Talk Now
                </button>
              </div>

              {/* Compact Quote Button */}
              <Button
                size="sm"
                className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white font-medium transition-all duration-300 whitespace-nowrap hover:scale-105 text-xs px-4 py-2"
                onClick={handleGetQuote}
              >
                <i className="ri-phone-line mr-1"></i>
                Get Quote
              </Button>
            </div>

            {/* Compact Mobile Menu Button */}
            <button
              className="lg:hidden rounded-lg bg-slate-800/60 border border-cyan-500/30 transition-all duration-300 p-2 hover:scale-105 hover:bg-slate-700/80 group"
              onClick={toggleMenu}
            >
              <i
                className={`text-cyan-400 transition-all duration-300 text-lg ${
                  isMenuOpen ? 'ri-close-line' : 'ri-menu-4-line'
                }`}
              ></i>
            </button>
          </div>

          {/* Existing Mobile Navigation */}
          <div className={`lg:hidden transition-all duration-300 overflow-hidden ${
            isMenuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
          }`}>
            <div className="py-3 space-y-2 border-t border-cyan-500/20">
              {[
                { id: 'home', label: 'Home', icon: 'ri-home-4-line' },
                { id: 'services', label: 'Services', icon: 'ri-flashlight-line' },
                { id: 'about', label: 'About', icon: 'ri-information-line' },
                { id: 'products', label: 'Products', icon: 'ri-car-line' },
                { id: 'contact', label: 'Contact', icon: 'ri-customer-service-line' }
              ].map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`flex items-center w-full text-left px-4 py-3 font-medium transition-all duration-300 rounded-lg mx-2 hover:scale-105 group ${
                    activeSection === item.id
                      ? 'text-white bg-gradient-to-r from-cyan-600/40 to-blue-600/40'
                      : 'text-gray-300 hover:text-white hover:bg-white/10'
                  }`}
                >
                  <div className={`w-3 h-3 rounded-full mr-3 transition-all duration-300 ${
                    activeSection === item.id ? 'bg-cyan-400 animate-pulse' : 'bg-gray-600'
                  }`}></div>
                  <div className="w-5 h-5 flex items-center justify-center mr-3 relative">
                    <i className={`${item.icon} animate-pulse group-hover:animate-bounce ${
                      activeSection === item.id ? 'text-cyan-300' : ''
                    }`}></i>
                    {activeSection === item.id && (
                      <div className="absolute inset-0 bg-cyan-400/30 rounded-full animate-ping"></div>
                    )}
                  </div>
                  {item.label}
                </button>
              ))}

              <div className="px-4 py-3 space-y-3 border-t border-cyan-500/20 mt-3">
                <div
                  className="flex items-center text-sm text-gray-300 cursor-pointer hover:text-cyan-300 transition-colors duration-300 group"
                  onClick={handleEmailClick}
                >
                  <div className="w-5 h-5 flex items-center justify-center mr-3 relative">
                    <i className="ri-mail-line text-cyan-400 animate-pulse group-hover:animate-bounce"></i>
                    <div className="absolute inset-0 bg-cyan-400/20 rounded-full opacity-0 group-hover:opacity-100 animate-ping"></div>
                  </div>
                  <span>info@djthaika.in</span>
                </div>

                <div
                  className="flex items-center text-sm text-gray-300 cursor-pointer hover:text-green-300 transition-colors duration-300 group"
                  onClick={handlePhoneClick}
                >
                  <div className="w-5 h-5 flex items-center justify-center mr-3 relative">
                    <i className="ri-phone-line text-green-400 animate-pulse group-hover:animate-bounce"></i>
                    <div className="absolute inset-0 bg-green-400/20 rounded-full opacity-0 group-hover:opacity-100 animate-ping"></div>
                  </div>
                  <span>1-800-570-0229</span>
                </div>

                {/* Mobile Expert Section */}
                <div className="flex items-center space-x-2 p-3 bg-gradient-to-r from-green-600/20 to-emerald-600/20 rounded-lg border border-green-500/30">
                  <div className="w-6 h-6 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center relative">
                    <i className="ri-user-star-line text-white text-xs animate-pulse"></i>
                    <div className="absolute -top-1 -right-1 w-2 h-2 bg-green-400 rounded-full animate-pulse opacity-75"></div>
                  </div>
                  <div className="flex-1">
                    <p className="text-white text-xs font-semibold">Expert Available - 24/7 Support</p>
                  </div>
                </div>

                <Button
                  size="sm"
                  className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-medium hover:scale-105 transition-all duration-300 group relative overflow-hidden"
                  onClick={handleContactExpert}
                >
                  <div className="w-4 h-4 flex items-center justify-center mr-2 relative">
                    <i className="ri-customer-service-2-line animate-pulse group-hover:animate-bounce"></i>
                    <div className="absolute inset-0 bg-white/20 rounded-full opacity-0 group-hover:opacity-100 animate-ping"></div>
                  </div>
                  Talk to Expert
                  <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-white/5 transform translate-x-full group-hover:translate-x-0 transition-transform duration-500"></div>
                </Button>

                <Button
                  size="sm"
                  className="w-full bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white font-medium hover:scale-105 transition-all duration-300 group relative overflow-hidden"
                  onClick={handleGetQuote}
                >
                  <div className="w-4 h-4 flex items-center justify-center mr-2 relative">
                    <i className="ri-phone-line animate-pulse group-hover:animate-bounce"></i>
                    <div className="absolute inset-0 bg-white/20 rounded-full opacity-0 group-hover:opacity-100 animate-ping"></div>
                  </div>
                  Get Free Quote
                  <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-white/5 transform translate-x-full group-hover:translate-x-0 transition-transform duration-500"></div>
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Enhanced Header Animation Styles */}
        <style jsx>{`
          .logo-container {
            position: relative;
            overflow: visible;
          }
          
          /* Enhanced logo hover effect with multiple layers */
          .logo-container::before {
            content: '';
            position: absolute;
            top: 50%;
            left: 50%;
            width: 100px;
            height: 100px;
            background: radial-gradient(circle, rgba(34, 211, 238, 0.2) 0%, rgba(59, 130, 246, 0.15) 50%, transparent 70%);
            border-radius: 50%;
            transform: translate(-50%, -50%) scale(0);
            transition: all 0.8s cubic-bezier(0.4, 0, 0.2, 1);
            opacity: 0;
            z-index: -1;
          }
          
          .logo-container:hover::before {
            transform: translate(-50%, -50%) scale(2);
            opacity: 1;
          }
          
          /* Enhanced Particle Glow Effects */
          .particle-glow {
            filter: drop-shadow(0 0 3px currentColor);
            animation-timing-function: ease-in-out;
          }
          
          .particle-glow:hover {
            filter: drop-shadow(0 0 6px currentColor) brightness(1.5);
          }
          
          /* Enhanced Energy Pulse Effects */
          .energy-pulse {
            animation: energy-pulse-wave 4s infinite linear;
          }
          
          @keyframes energy-pulse-wave {
            0% { 
              opacity: 0.2; 
              transform: translateX(-100%) scaleY(1);
              filter: blur(0px);
            }
            25% { 
              opacity: 0.8; 
              transform: translateX(-25%) scaleY(1.5);
              filter: blur(0.5px);
            }
            50% { 
              opacity: 1; 
              transform: translateX(50%) scaleY(2);
              filter: blur(1px);
            }
            75% { 
              opacity: 0.6; 
              transform: translateX(125%) scaleY(1.2);
              filter: blur(0.5px);
            }
            100% { 
              opacity: 0; 
              transform: translateX(200%) scaleY(1);
              filter: blur(0px);
            }
          }
          
          /* Tech Ring Glow Animation */
          .tech-ring-glow {
            animation: tech-ring-pulse 3s infinite ease-in-out;
          }
          
          @keyframes tech-ring-pulse {
            0%, 100% { 
              opacity: 0.1;
              filter: drop-shadow(0 0 0 currentColor) brightness(1);
              transform: scale(1) rotate(0deg);
            }
            33% { 
              opacity: 0.4;
              filter: drop-shadow(0 0 4px currentColor) brightness(1.3);
              transform: scale(1.1) rotate(120deg);
            }
            66% { 
              opacity: 0.3;
              filter: drop-shadow(0 0 2px currentColor) brightness(1.1);
              transform: scale(0.95) rotate(240deg);
            }
          }
          
          /* Data Stream Animations */
          .data-stream {
            animation: data-flow-right 6s infinite linear;
          }
          
          .data-stream-reverse {
            animation: data-flow-left 8s infinite linear;
          }
          
          @keyframes data-flow-right {
            0% { 
              transform: translateX(-100px) scaleX(0);
              opacity: 0;
            }
            10% { 
              transform: translateX(-50px) scaleX(0.5);
              opacity: 0.5;
            }
            50% { 
              transform: translateX(50px) scaleX(1);
              opacity: 1;
            }
            90% { 
              transform: translateX(150px) scaleX(0.3);
              opacity: 0.3;
            }
            100% { 
              transform: translateX(200px) scaleX(0);
              opacity: 0;
            }
          }
          
          @keyframes data-flow-left {
            0% { 
              transform: translateX(100px) scaleX(0);
              opacity: 0;
            }
            10% { 
              transform: translateX(50px) scaleX(0.5);
              opacity: 0.5;
            }
            50% { 
              transform: translateX(-50px) scaleX(1);
              opacity: 1;
            }
            90% { 
              transform: translateX(-150px) scaleX(0.3);
              opacity: 0.3;
            }
            100% { 
              transform: translateX(-200px) scaleX(0);
              opacity: 0;
            }
          }
          
          /* Enhanced Circuit Pattern Animation */
          .header-circuit-pattern-enhanced {
            background-image: 
              radial-gradient(circle at 25% 25%, rgba(34, 211, 238, 0.08) 1px, transparent 1px),
              radial-gradient(circle at 75% 75%, rgba(59, 130, 246, 0.06) 1px, transparent 1px),
              radial-gradient(circle at 50% 50%, rgba(16, 185, 129, 0.05) 1px, transparent 1px),
              linear-gradient(rgba(34, 211, 238, 0.03) 1px, transparent 1px),
              linear-gradient(90deg, rgba(16, 185, 129, 0.03) 1px, transparent 1px);
            background-size: 15px 15px, 20px 20px, 12px 12px, 8px 8px, 8px 8px;
            width: 100%;
            height: 100%;
            animation: circuit-flow-enhanced 25s infinite linear;
          }
          
          @keyframes circuit-flow-enhanced {
            0% { 
              transform: translate(0, 0) rotate(0deg);
              opacity: 0.3;
            }
            25% { 
              transform: translate(4px, 4px) rotate(0.5deg);
              opacity: 0.6;
            }
            50% { 
              transform: translate(8px, 8px) rotate(0deg);
              opacity: 0.4;
            }
            75% { 
              transform: translate(12px, 12px) rotate(-0.5deg);
              opacity: 0.7;
            }
            100% { 
              transform: translate(15px, 15px) rotate(0deg);
              opacity: 0.3;
            }
          }
          
          /* Network Node Pulse Animation */
          .network-node {
            animation: network-pulse 4s infinite ease-in-out;
          }
          
          @keyframes network-pulse {
            0%, 100% { 
              transform: scale(1);
              opacity: 0.3;
              box-shadow: 0 0 0 0 currentColor;
            }
            25% { 
              transform: scale(1.5);
              opacity: 0.8;
              box-shadow: 0 0 8px 2px currentColor;
            }
            50% { 
              transform: scale(2);
              opacity: 1;
              box-shadow: 0 0 12px 4px currentColor;
            }
            75% { 
              transform: scale(1.3);
              opacity: 0.6;
              box-shadow: 0 0 6px 1px currentColor;
            }
          }
          
          /* Connecting Lines Animation */
          .connecting-line-1 {
            position: absolute;
            width: 30px;
            height: 1px;
            background: linear-gradient(90deg, rgba(34, 211, 238, 0.4), transparent);
            transform-origin: left center;
            animation: connecting-line-grow 5s infinite ease-in-out;
          }
          
          .connecting-line-2 {
            position: absolute;
            width: 25px;
            height: 1px;
            background: linear-gradient(270deg, rgba(16, 185, 129, 0.3), transparent);
            transform-origin: right center;
            animation: connecting-line-grow 6s infinite ease-in-out;
            animation-delay: 2s;
          }
          
          .connecting-line-3 {
            position: absolute;
            width: 20px;
            height: 1px;
            background: linear-gradient(90deg, rgba(59, 130, 246, 0.4), transparent);
            transform-origin: center;
            animation: connecting-line-grow 4s infinite ease-in-out;
            animation-delay: 1s;
          }
          
          @keyframes connecting-line-grow {
            0%, 100% { 
              transform: scaleX(0);
              opacity: 0;
            }
            20% { 
              transform: scaleX(0.3);
              opacity: 0.5;
            }
            50% { 
              transform: scaleX(1);
              opacity: 1;
            }
            80% { 
              transform: scaleX(0.7);
              opacity: 0.7;
            }
          }

          /* Header Energy Line Animations Enhanced */
          @keyframes header-energy-line-right {
            0% { 
              transform: translateX(-100%) scaleY(1);
              opacity: 0;
              filter: blur(0px);
            }
            25% { 
              transform: translateX(-25%) scaleY(2);
              opacity: 0.8;
              filter: blur(0.5px);
            }
            50% { 
              transform: translateX(50%) scaleY(3);
              opacity: 1;
              filter: blur(1px);
            }
            75% { 
              transform: translateX(125%) scaleY(1.5);
              opacity: 0.6;
              filter: blur(0.5px);
            }
            100% { 
              transform: translateX(200vw) scaleY(1);
              opacity: 0;
              filter: blur(0px);
            }
          }

          @keyframes header-energy-line-left {
            0% { 
              transform: translateX(100%) scaleY(1);
              opacity: 0;
              filter: blur(0px);
            }
            25% { 
              transform: translateX(25%) scaleY(2);
              opacity: 0.7;
              filter: blur(0.5px);
            }
            50% { 
              transform: translateX(-50%) scaleY(2.5);
              opacity: 1;
              filter: blur(1px);
            }
            75% { 
              transform: translateX(-125%) scaleY(1.2);
              opacity: 0.5;
              filter: blur(0.5px);
            }
            100% { 
              transform: translateX(-200vw) scaleY(1);
              opacity: 0;
              filter: blur(0px);
            }
          }

          .header-energy-line-right {
            animation: header-energy-line-right 8s infinite ease-in-out;
          }

          .header-energy-line-left {
            animation: header-energy-line-left 10s infinite ease-in-out;
          }

          /* Enhanced logo text animation */
          @keyframes logo-text-glow {
            0%, 100% { 
              text-shadow: 0 0 5px rgba(34, 211, 238, 0.3), 0 0 10px rgba(59, 130, 246, 0.2);
              filter: brightness(1) contrast(1.1);
            }
            50% { 
              text-shadow: 0 0 15px rgba(34, 211, 238, 0.6), 0 0 25px rgba(59, 130, 246, 0.4), 0 0 35px rgba(16, 185, 129, 0.3);
              filter: brightness(1.3) contrast(1.2);
            }
          }
          
          .logo-text-animation:hover {
            animation: logo-text-glow 2s infinite ease-in-out;
          }
          
          /* Enhanced logo pulse effect */
          @keyframes logo-container-pulse {
            0%, 100% { 
              transform: scale(1) rotate(0deg); 
              filter: brightness(1) drop-shadow(0 0 0 transparent);
            }
            25% { 
              transform: scale(1.02) rotate(0.5deg); 
              filter: brightness(1.1) drop-shadow(0 0 8px rgba(34, 211, 238, 0.3));
            }
            50% { 
              transform: scale(1.05) rotate(0deg); 
              filter: brightness(1.2) drop-shadow(0 0 15px rgba(34, 211, 238, 0.4));
            }
            75% { 
              transform: scale(1.02) rotate(-0.5deg); 
              filter: brightness(1.1) drop-shadow(0 0 8px rgba(34, 211, 238, 0.3));
            }
          }
          
          .logo-container:hover > div {
            animation: logo-container-pulse 3s infinite ease-in-out;
          }
          
          /* Enhanced energy flow around logo */
          @keyframes energy-orbit {
            0% { 
              opacity: 0; 
              transform: rotate(0deg) translateX(25px) rotate(0deg);
            }
            50% { 
              opacity: 1; 
            }
            100% { 
              opacity: 0; 
              transform: rotate(360deg) translateX(25px) rotate(-360deg);
            }
          }
          
          .logo-container::after {
            content: '';
            position: absolute;
            top: 50%;
            left: 50%;
            width: 3px;
            height: 8px;
            background: linear-gradient(45deg, #22d3ee, #3b82f6, #10b981);
            transform: translate(-50%, -50%);
            border-radius: 50%;
            opacity: 0;
            animation: energy-orbit 4s infinite linear;
          }
          
          .logo-container:hover::after {
            opacity: 1;
          }

          /* Icon Hover Effects Enhanced */
          @keyframes icon-glow {
            0%, 100% { 
              text-shadow: 0 0 0 currentColor;
              transform: scale(1);
            }
            50% { 
              text-shadow: 0 0 8px currentColor, 0 0 12px currentColor;
              transform: scale(1.1);
            }
          }

          .group:hover i {
            animation: icon-glow 1.5s infinite ease-in-out;
          }

          /* Navigation Button Effects Enhanced */
          @keyframes nav-button-pulse {
            0%, 100% { 
              box-shadow: 0 0 0 0 rgba(34, 211, 238, 0.4);
              transform: scale(1);
            }
            50% { 
              box-shadow: 0 0 20px 5px rgba(34, 211, 238, 0.1);
              transform: scale(1.02);
            }
          }

          .group:hover button {
            animation: nav-button-pulse 2s infinite;
          }

          /* Header Hover Effects */
          header:hover .particle-glow {
            animation-duration: 1.5s;
            filter: drop-shadow(0 0 4px currentColor) brightness(1.3);
          }

          header:hover .tech-glow {
            animation-duration: 2s;
            opacity: 0.4;
          }

          header:hover .energy-pulse {
            animation-duration: 2s;
          }

          /* Responsive Animation Adjustments */
          @media (max-width: 768px) {
            .particle-glow {
              animation-duration: 2s;
            }
            
            .tech-ring-glow {
              animation-duration: 4s;
            }
            
            .energy-pulse {
              animation-duration: 3s;
            }
          }
        `}</style>
      </header>

      {/* Enhanced Expert Contact Modal */}
      {showExpertModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full mx-4 p-6 md:p-8 relative animate-scale-in shadow-2xl max-h-[90vh] overflow-y-auto">
            <button
              onClick={() => setShowExpertModal(false)}
              className="absolute top-4 right-4 w-10 h-10 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center text-white transition-all duration-300 cursor-pointer shadow-lg hover:shadow-xl z-10"
            >
              <i className="ri-close-line text-lg"></i>
            </button>
            
            <div className="text-center">
              <div className="w-16 md:w-20 h-16 md:h-20 bg-gradient-to-br from-green-500 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4 md:mb-6 relative">
                <i className="ri-user-star-line text-2xl md:text-3xl text-white"></i>
                <div className="absolute -top-2 -right-2 w-6 h-6 bg-green-400 rounded-full animate-pulse">
                  <div className="absolute inset-0 bg-green-400 rounded-full animate-ping opacity-60"></div>
                </div>
              </div>
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3 md:mb-4">Connect with Our EV Expert</h3>
              <p className="text-base md:text-lg text-gray-600 mb-6 md:mb-8 leading-relaxed px-2">Get instant expert guidance for your EV charging needs. Our specialists are available 24/7 to help you make the right choice.</p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4 mb-4 md:mb-6">
                <button 
                  className="flex items-center justify-center space-x-2 md:space-x-3 bg-green-600 hover:bg-green-700 py-3 md:py-4 px-4 md:px-6 rounded-xl text-white font-semibold transition-all duration-300 transform hover:scale-105 cursor-pointer shadow-lg hover:shadow-xl text-sm md:text-base"
                  onClick={handlePhoneCall}
                >
                  <i className="ri-phone-line text-lg md:text-xl"></i>
                  <div className="text-left">
                    <div className="text-sm font-medium">Call Now</div>
                    <div className="text-xs opacity-90">1-800-570-0229</div>
                  </div>
                </button>
                
                <button 
                  className="flex items-center justify-center space-x-2 md:space-x-3 bg-purple-600 hover:bg-purple-700 py-3 md:py-4 px-4 md:px-6 rounded-xl text-white font-semibold transition-all duration-300 transform hover:scale-105 cursor-pointer shadow-lg hover:shadow-xl text-sm md:text-base"
                  onClick={handleEmailExpert}
                >
                  <i className="ri-mail-line text-lg md:text-xl"></i>
                  <div className="text-left">
                    <div className="text-sm font-medium">Email Expert</div>
                    <div className="text-xs opacity-90">Quick Response</div>
                  </div>
                </button>
                
                <button 
                  className="flex items-center justify-center space-x-2 md:space-x-3 bg-cyan-600 hover:bg-cyan-700 py-3 md:py-4 px-4 md:px-6 rounded-xl text-white font-semibold transition-all duration-300 transform hover:scale-105 cursor-pointer shadow-lg hover:shadow-xl text-sm md:text-base"
                  onClick={handleLiveChat}
                >
                  <i className="ri-chat-3-line text-lg md:text-xl"></i>
                  <div className="text-left">
                    <div className="text-sm font-medium">Live Chat</div>
                    <div className="text-xs opacity-90">AI Assistant</div>
                  </div>
                </button>
                
                <button 
                  className="flex items-center justify-center space-x-2 md:space-x-3 bg-blue-600 hover:bg-blue-700 py-3 md:py-4 px-4 md:px-6 rounded-xl text-white font-semibold transition-all duration-300 transform hover:scale-105 cursor-pointer shadow-lg hover:shadow-xl text-sm md:text-base"
                  onClick={handleWhatsAppChat}
                >
                  <i className="ri-whatsapp-line text-lg md:text-xl"></i>
                  <div className="text-left">
                    <div className="text-sm font-medium">WhatsApp</div>
                    <div className="text-xs opacity-90">Instant Chat</div>
                  </div>
                </button>
              </div>
              
              <button 
                className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 py-3 md:py-4 px-4 md:px-6 rounded-xl text-white font-bold text-base md:text-lg transition-all duration-300 transform hover:scale-105 cursor-pointer shadow-lg hover:shadow-xl mb-4 md:mb-6"
                onClick={handleScheduleCall}
              >
                <i className="ri-calendar-line mr-2 md:mr-3 text-lg md:text-xl"></i>
                Schedule Site Visit & Consultation
              </button>
              
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-4 md:p-6 border border-green-200">
                <div className="flex items-center justify-center mb-3">
                  <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse mr-3"></div>
                  <p className="text-base md:text-lg text-green-800 font-bold">Expert Available Now</p>
                </div>
                <div className="grid grid-cols-2 gap-4 text-xs md:text-sm text-green-700 mb-3">
                  <div className="text-center">
                    <div className="font-semibold">Response Time</div>
                    <div className="text-green-600">Under 2 minutes</div>
                  </div>
                  <div className="text-center">
                    <div className="font-semibold">Consultation</div>
                    <div className="text-green-600">100% Free</div>
                  </div>
                </div>
                <div className="flex flex-wrap items-center justify-center gap-2 md:gap-4 text-xs text-green-600">
                  <span className="flex items-center">
                    <i className="ri-check-line mr-1"></i>
                    5+ Years Experience
                  </span>
                  <span className="flex items-center">
                    <i className="ri-check-line mr-1"></i>
                    1500+ Projects
                  </span>
                  <span className="flex items-center">
                    <i className="ri-check-line mr-1"></i>
                    24/7 Support
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes scale-in {
          from { opacity: 0; transform: scale(0.9); }
          to { opacity: 1; transform: scale(1); }
        }
        
        .animate-scale-in {
          animation: scale-in 0.3s ease-out;
        }
      `}</style>
    </>
  );
}
